import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import type { CategoriaDocumento, DocumentoDelCatalogo } from "@/lib/informes";
import { cn } from "@/lib/utils";

/**
 * Lo que una fila necesita para ofrecer clasificar o reclasificar un archivo.
 *
 * Viaja como UN objeto opcional por toda la cadena de componentes (pestaña →
 * fila del documento → lista de archivos → fila del archivo) en vez de cuatro
 * props sueltas: si falta, la fila simplemente no ofrece la acción, y ningún
 * llamador antiguo tiene que enterarse.
 */
export interface Clasificacion {
  /** El catálogo de LAS DOS categorías, no solo el de la pestaña. */
  catalogos: Record<CategoriaDocumento, DocumentoDelCatalogo[]>;
  /** La categoría de la pestaña donde está parada la persona: va primero. */
  categoriaActual: CategoriaDocumento;
  /**
   * Clasifica el archivo (`null` lo devuelve a "sin clasificar"). Resuelve
   * `true` si el servidor lo aceptó; con `false` el selector queda abierto para
   * que la persona elija otra cosa, porque el motivo ya se le mostró.
   */
  onClasificar: (archivoId: number, documentoTipoId: number | null) => Promise<boolean>;
}

const TITULO_DE_LA_CATEGORIA: Record<CategoriaDocumento, string> = {
  legal: "Antecedentes legales",
  historico: "Antecedentes históricos",
};

/**
 * El selector de tipo de documento de UN archivo, en línea dentro de su fila.
 *
 * ══ POR QUÉ OFRECE LAS DOS CATEGORÍAS ═══════════════════════════════════════
 *
 * El cliente deja lo que mandó en tres espacios que no coinciden uno a uno con
 * las dos pestañas: "tasación anterior externa" es un documento LEGAL (se le
 * pide en la carta del presupuesto) y el cliente la sube en "Tasaciones
 * anteriores", que se revisa en históricos. Limitar la lista a la categoría de
 * la pestaña dejaría ese archivo sin un tipo al que moverlo. La de la pestaña
 * va primero, que es lo que casi siempre se busca; la otra, bajo su título.
 *
 * ══ UNA ELECCIÓN EXPLÍCITA, NUNCA AL TOCAR ═════════════════════════════════
 *
 * Clasificar es lo que le da el archivo a la IA y a un documento del
 * expediente, así que elegir marca la opción y "Guardar" la manda; sin
 * elección, "Guardar" está apagado. Es el mismo patrón que la clasificación
 * de las capturas de terreno del diseño.
 */
export function SelectorDeClasificacion({
  nombreDelArchivo,
  clasificacion,
  excluirTipoId = null,
  onClasificar,
  onCerrar,
}: {
  nombreDelArchivo: string;
  clasificacion: Pick<Clasificacion, "catalogos" | "categoriaActual">;
  /** El tipo actual del archivo, que no tiene sentido volver a ofrecer. */
  excluirTipoId?: number | null;
  onClasificar: (documentoTipoId: number) => Promise<boolean>;
  onCerrar: () => void;
}) {
  const [elegido, setElegido] = useState<number | null>(null);
  const [guardando, setGuardando] = useState(false);

  const otra: CategoriaDocumento =
    clasificacion.categoriaActual === "legal" ? "historico" : "legal";
  const grupos = [clasificacion.categoriaActual, otra]
    .map((categoria) => ({
      categoria,
      documentos: (clasificacion.catalogos[categoria] ?? []).filter((d) => d.id !== excluirTipoId),
    }))
    .filter((g) => g.documentos.length > 0);

  async function guardar() {
    if (elegido === null) return;

    setGuardando(true);
    try {
      if (await onClasificar(elegido)) onCerrar();
    } finally {
      setGuardando(false);
    }
  }

  return (
    <div className="bg-card mt-2 flex flex-col gap-2.5 rounded-[0.625rem] border-[1.5px] p-3">
      <div
        role="radiogroup"
        aria-label={`Tipo de documento de ${nombreDelArchivo}`}
        className="flex flex-col gap-2.5"
      >
        {grupos.map(({ categoria, documentos }) => (
          <div key={categoria} className="flex flex-col gap-1.5">
            <span className="text-ink-hint text-[0.65625rem] font-extrabold tracking-[0.03em] uppercase">
              {TITULO_DE_LA_CATEGORIA[categoria]}
            </span>
            <div className="flex flex-wrap gap-1.5">
              {documentos.map((documento) => {
                const marcado = elegido === documento.id;

                return (
                  <button
                    key={documento.id}
                    type="button"
                    role="radio"
                    aria-checked={marcado}
                    disabled={guardando}
                    onClick={() => setElegido(documento.id)}
                    className={cn(
                      "rounded-lg border-[1.5px] px-2.5 py-1 text-left text-xs transition-colors disabled:opacity-60",
                      marcado
                        ? "border-primary bg-success-soft text-ink font-bold"
                        : "border-input text-ink-body hover:border-ink-ghost font-semibold",
                    )}
                  >
                    {documento.nombre}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={guardando}
          aria-label={`Cancelar clasificación de ${nombreDelArchivo}`}
          onClick={onCerrar}
        >
          Cancelar
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={elegido === null || guardando}
          aria-label={`Guardar clasificación de ${nombreDelArchivo}`}
          onClick={() => void guardar()}
        >
          {guardando ? <Spinner /> : null}
          Guardar
        </Button>
      </div>
    </div>
  );
}
